import { AnalyticsUpsellType } from '@/services/analytics/platforms/adobe-analytics/models';
import { CountryCode } from '@/store/ui';

export interface CmsImageUrls {
  default: string;
  desktop?: string;
  mobile?: string;
}

export enum CmsLinkLocation {
  Internal = 'internal', // vue route, e.g. /rewards
  External = 'external', // external to Vue, e.g. AEM pages and https://catering.chipotle.com
}

export enum CmsLinkTarget {
  Blank = '_blank',
  Self = '_self',
}

export type CmsLink =
  | {
      type: 'text';
      text: string;
      url: string;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
    }
  | {
      type: 'button'; 
      text: string;
      enabled: boolean;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
      url: string;
    }
  | {
      type: 'icon' | 'image';
      url: string;
      imageUrl: string;
      altText: string;
      location: CmsLinkLocation;
      target: CmsLinkTarget;
    };

export interface PromoBanner {
  showPromoBanner: boolean;
  showAdaptiveHero?: boolean | null;
  nooHomePageURL?: string;
  showNewHero?: boolean | null;
  showHero?: boolean | null;
  dualPromo: boolean;
  centerAlignedTextOnly: boolean;
  isContentRightAligned: boolean;
  shouldPlaceMobileTitleBelowImage: boolean;
  primary: {
    backgroundImageUrl?: string;
    backgroundVideoUrl?: string;
    shouldLoopVideo?: boolean;
    title?: string;
    preTitle?: string;
    preTitleColor?: string;
    titleColor?: string;
    subtitle?: string;
    subtitleColor?: string;
    legalText?: string;
    legalTextColor?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonColor?: string;
    imageUrlDesktop?: string;
    imageUrlMobile?: string;
    videoUrlDesktop?: string;
    videoUrlMobile?: string;
    shouldLoopBottomVideo?: boolean;
    linkCategory?: string;
  };
  secondary?: {
    title?: string;
    titleColor?: string;
    subtitle?: string;
    subtitleColor?: string;
    legalText?: string;
    legalTextColor?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonColor?: string;
    imageUrlDesktop?: string;
    imageUrlMobile?: string;
    videoUrlDesktop?: string;
    videoUrlMobile?: string;
    shouldLoopBottomVideo?: boolean;
    linkCategory?: string;
  };
}

export interface LargeOrderPromo {
  desktopBackgroundImageUrl?: string;
  mobileBackgroundImageUrl?: string;
  mobileBackgroundColor?: string;
  desktopBackgroundColor?: string;
  preTitle?: string;
  preTitleColor?: string;
  title?: string;
  titleColor?: string;
}

export interface GuacMode {
  message?: string;
}

export interface RewardsBanner {
  enableRewardsBanner: boolean;
  medallionImage: string;
  description: string;
  ctaSelecter: 'Standard' | 'DynamicLink';
  createAccountButtonLabel: string;
  dynamicCtaUrl: string;
  dynamicCtaButtonLabel: string;
  signInButtonLabel: string;
}

export interface LoyaltyInterruptModal {
  subCopy: string;
  salutation: string;
  howRewardsWorksCta: string;
  medallionImageUrl: string;
  mainCopy: string;
  joinNowCta: string;
}

export interface LargeOrderPromoCard {
  title?: string;
  titleColor?: string;
  backgroundColor?: string;
  imageUrl?: string;
  navUrl?: string;
  detailsCopy?: string;
  detailsCopyColor?: string;
  boldCopy?: string;
  boldCopyColor?: string;
  buttonText?: string;
  buttonTextColor?: string;
  buttonColor?: string;
  linkCategory?: string;
}

export interface JoinRewards {
  header: string;
  buttonShortText: string;
  buttonLongText: string;
  emailUpdates: string;
}

export interface LegalCopy {
  createAnAccount: string,
  createAnAccountOrderConfirmation: string;
  createAnAccount2sv: string;
  signIn: string;
  updateProfile: string;
  personalAndPreference: string;
  loggedInUnEnrolled: string;
  retroCredit: string;
  nutritionCalculatorBuilderDisclaimer: string;
  chooseYourOwnBirthday: string;
}

export type LegalCopyCreateAccount = {
  [key in CountryCode]: string;
}

export interface Footer {
  primaryLinks: CmsLink[];
  secondaryLinks: {
    heading: string;
    links: CmsLink[];
  }[];
  tertiaryLinks: CmsLink[];
  cookiePrefs: CmsLink;
}

export interface UpsellItem {
  cmgId: string;
  MenuItemName?: string;
  imageUrl: string;
  sortOrderPosition?: number;
  shortName: string;
  name: string;
  price: string;
  upsellType: AnalyticsUpsellType;
}

export interface CustomerCare {
  message: string;
  linkText: string;
  linkUrl: string;
}

export interface SystemFlags {
  headerPages: string[];
}

export interface CreateAccountSignInContentFragment {
  isModalEnabled: boolean;
  heading?: string;
  subheading?: string;
  imageUrl?: string;
  text1?: string;
  text2?: string;
  p1?: string;
  p2?: string;
  buttonText?: string;
  buttonUrl?: string;
  phoneDisclaimer?: string;
}

export interface AccountOptInToolTipsContentFragment {
  rewardsTitle?: string;
  rewardsBodyText?: string;
  emailTitle?: string;
  emailBodyText?: string;
  smsTitle?: string;
  smsBodyText?: string;
}

export interface NutritionFactsContentFragment {
  label?: string;
  path?: string;
}

export interface AccountModalContentFragment {
  linkForFavoriteRecentOrders?: string;
  guestAccountCreationHeadline?: string;
}

export interface OrderReviewModalContentFragment {
  title?: string;
  primaryCta?: string;
  secondaryCta?: string;
  countdownTimer?: number;
}

export interface ScanlessIntroductionContentFragment {
  isModalEnabled: boolean;
  heading?: string;
  bodyHtml?: string;
  footerHtml?: string;
  imageUrl?: string;
}

export interface ScanlessCreditCardAlreadyAssociatedContentFragment {
  isModalEnabled: boolean;
  title?: string;
  body?: string;
}

export interface CyobRewardContentFragment {
  title?: string;
  subtitle?: string;
}

export interface ReferAFriendFooterContentFragment {
  title?: string;
  body?: string;
  ctaButton?: string;
}

export interface ReferAFriendModalContentFragment {
  title?: string;
  subtitle?: string;
  primaryCta?: string;
  termsAndConditionsSubheader?: string;
  termsAndConditionsBody?: string;
}

export interface AlreadyAMemberModalContentFragment {
  title?: string;
  subtitle?: string; 
  bodyText1?: string;
  imageUrl1?: string;
  bodyText2?: string;
  imageUrl2?: string;
  bodyText3?: string;
  imageUrl3?: string;
  primaryCta?: string;
  secondaryCta?: string;
}

export interface YoureInvitedModalContentFragment {
  title?: string;
  subtitle?: string;  
  legalCopy?: string;
}

export interface ReferralJoinSuccessContentFragment {
  title?: string;
  subtitle?: string;
  bodyText?: string;
  primaryCta?: string;
}

export interface ReferralNotEligibleContentFragment {
  title?: string;
  subtitle?: string;
  description?: string;
  legalCopy?: string;
}

export interface FeaturedPCE {
  header: string;
  subHeader: string;
  restaurantSelectedHeader: string;
}

// Currently used in Order WEb <title> tag and <meta rel="description"> tag 
export interface OrderWebMetadata {
    title: string;
    description: string;
}

// Currently used in Order WEb <title> tag and <meta rel="description"> tag for Google SEO/search results
export interface OrderWebTitleDescription {
  title: string;
  description: string;
}

export interface RetroCreditImages {
  receiptZip: CmsImageUrls;
  receiptDate: CmsImageUrls;
  receiptCheckNumber: CmsImageUrls;
  receiptTotal: CmsImageUrls;
}

export interface PickupModality {
  dineInNotOpen: string;
  pickupOptions: PickupModalityOption[];
}

export interface PickupModalityOption {
  title: string;
  description: string;
  imageUrl: string;
}

export interface OrderConfirmationCreateAccountExperience {
  header: string;
  description: string;
  medallionImageUrl: string;
}

export interface FacModalContent {
  header: string;
  pickupToggleIconUrl: string;
  pickupText: string;
  deliveryToggleIconUrl: string;
  deliveryText: string;
  pickupSearchBar: string;
  favoriteHeader: string;
  favoriteBody: string;
}

export interface TaxContent {
  itemizedTaxLine: string;
  vatRate: string;
  registrationNumberText: string;
  registrationNumber: string;
}

export interface BagContent {
  multiplePromosAvailable: string;
  promoCodeText: string;
  promoAddedToEmptyBag: string;
  rewardsAvailableText: string;
  emptyBagImageUrl: string;
  viewPromoModal: string;
  viewPromoModalDisclaimer: string;
}

export interface ReferAFriendLinkOpenedContentFragment {
  title?: string;
  subtitle?: string;
  legalCopy?: string;
}

export interface SummerOfExtrasIntroScreensContentFragment {
  title?: string;
  bodySlide1?: string;
  description1?: string;
  description2?: string;
  description3?: string;
  bodySlide3?: string;
}

export interface ChipotleuReVerificationContentFragment {
  subheadline?: string;
  headline?: string;
  body?: string;
  ctaText?: string;
}

export interface ChipotleuReVerificationSuccessModalContentFragment {
  headline?: string;
  ctaText?: string;
  image?: string;
  body?: string;
}
export interface EmployeeRewardsHowItWorksModalContentFragment {
  headline?: string;
  subHeadline?: string;
  bodyText?: string;
}